import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Stack, Typography, Chip, IconButton, Collapse, Skeleton,
  TextField, InputAdornment, Paper, Table, TableHead, TableRow, TableCell, TableBody, Tooltip, Card, Alert
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import api, { fetchRecords } from '../services/api';
import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';

export default function TaxGroupedView({ records, schema, onEdit, onDelete, onView, onAdd, tableName }) {
  const [expandedPersons, setExpandedPersons] = useState({});
  const [search, setSearch] = useState('');
  
  const [assignments, setAssignments] = useState([]);
  const [persons, setPersons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingAlerts, setPendingAlerts] = useState([]);
  const [hiddenAlerts, setHiddenAlerts] = useState(() => JSON.parse(sessionStorage.getItem('dismissedPendingTax') || '[]'));

  useEffect(() => {
    Promise.all([
      fetchRecords('ASSIGNMENT').catch(() => ({ data: { data: [] } })),
      fetchRecords('PERSON').catch(() => ({ data: { data: [] } }))
    ]).then(([a, p]) => {
      setAssignments(a.data.data || []);
      setPersons(p.data.data || []);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (tableName === 'TAX_DECLARATION') {
      api.get('/tax_declaration_pending').then(res => {
        if (res.data && res.data.pending) {
          setPendingAlerts(res.data.pending);
        }
      }).catch(err => console.error('Error fetching pending tax declarations:', err));
    }
  }, [tableName, records]);

  const dismissAlert = (assignment_id) => {
    const newHidden = [...hiddenAlerts, assignment_id];
    setHiddenAlerts(newHidden);
    sessionStorage.setItem('dismissedPendingTax', JSON.stringify(newHidden));
  };

  const assignmentMap = useMemo(() => Object.fromEntries(assignments.map(a => [a.assignment_id, a])), [assignments]);
  const personMap = useMemo(() => Object.fromEntries(persons.map(p => [p.person_id, p])), [persons]);

  const togglePerson = (personKey) => {
    setExpandedPersons(prev => ({
      ...prev,
      [personKey]: !prev[personKey]
    }));
  };

  const personGroups = useMemo(() => {
    const pGroup = {};
    
    for (const r of records) {
      const asnId = r.assignment_id || '—';
      const pId = r.person_id || '—';
      const personKey = `${pId}_${asnId}`;
      
      if (!pGroup[personKey]) {
        const asn = assignmentMap[asnId];
        const person = asn ? personMap[asn.person_id] : personMap[pId];
        const fullName = person ? [person.first_name, person.last_name].filter(Boolean).join(' ') : null;
        
        pGroup[personKey] = {
          personKey,
          person_id: pId,
          assignment_id: asnId,
          fullName: fullName || (asn ? asn.employee_code : 'Unknown Employee'),
          entries: []
        };
      }
      
      pGroup[personKey].entries.push(r);
    }
    
    return Object.values(pGroup).sort((a, b) => a.fullName.localeCompare(b.fullName));
  }, [records, assignmentMap, personMap]);

  const filteredGroups = useMemo(() => {
    if (!search.trim()) return personGroups;
    const q = search.toLowerCase();
    return personGroups.filter(g => 
      g.fullName.toLowerCase().includes(q) || 
      g.assignment_id.toLowerCase().includes(q)
    );
  }, [personGroups, search]);

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Skeleton variant="rectangular" width="100%" height={60} sx={{ borderRadius: 2 }} />
        <Skeleton variant="rectangular" width="100%" height={60} sx={{ borderRadius: 2 }} />
        <Skeleton variant="rectangular" width="100%" height={60} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  const renderHeaders = () => {
    if (tableName === 'TAX_DECLARATION') {
      return (
        <TableRow>
          <TableCell sx={{ fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', py: 1.5, borderBottom: '1.5px solid #E2E8F0' }}>Decl ID</TableCell>
          <TableCell sx={{ fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', py: 1.5, borderBottom: '1.5px solid #E2E8F0' }}>Fin Year</TableCell>
          <TableCell sx={{ fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', py: 1.5, borderBottom: '1.5px solid #E2E8F0' }}>Section</TableCell>
          <TableCell align="right" sx={{ fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', py: 1.5, borderBottom: '1.5px solid #E2E8F0' }}>Declared</TableCell>
          <TableCell align="right" sx={{ fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', py: 1.5, borderBottom: '1.5px solid #E2E8F0' }}>Approved</TableCell>
          <TableCell align="center" sx={{ fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', py: 1.5, borderBottom: '1.5px solid #E2E8F0' }}>Status</TableCell>
          <TableCell align="right" sx={{ fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', py: 1.5, borderBottom: '1.5px solid #E2E8F0' }}>Actions</TableCell>
        </TableRow>
      );
    }
    return (
      <TableRow>
        <TableCell sx={{ fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', py: 1.5, borderBottom: '1.5px solid #E2E8F0' }}>Config ID</TableCell>
        <TableCell sx={{ fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', py: 1.5, borderBottom: '1.5px solid #E2E8F0' }}>Fin Year</TableCell>
        <TableCell sx={{ fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', py: 1.5, borderBottom: '1.5px solid #E2E8F0' }}>Regime</TableCell>
        <TableCell align="right" sx={{ fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', py: 1.5, borderBottom: '1.5px solid #E2E8F0' }}>Projected Taxable</TableCell>
        <TableCell align="right" sx={{ fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', py: 1.5, borderBottom: '1.5px solid #E2E8F0' }}>TDS Monthly</TableCell>
        <TableCell align="center" sx={{ fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', py: 1.5, borderBottom: '1.5px solid #E2E8F0' }}>Locked</TableCell>
        <TableCell align="right" sx={{ fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', py: 1.5, borderBottom: '1.5px solid #E2E8F0' }}>Actions</TableCell>
      </TableRow>
    );
  };

  const renderStatus = (val) => {
    let color = '#64748B'; let bg = '#F1F5F9';
    if (val === 'APPROVED' || val === 'ELIGIBLE' || val === 'ACTIVE') { color = '#10B981'; bg = '#D1FAE5'; }
    else if (val === 'REJECTED' || val === 'NOT_ELIGIBLE') { color = '#EF4444'; bg = '#FEE2E2'; }
    else if (val === 'DRAFT' || val === 'CONDITIONAL' || val === 'PENDING') { color = '#F59E0B'; bg = '#FEF3C7'; }
    return <Chip label={val || 'Unknown'} size="small" sx={{ height: 20, fontSize: 10, fontWeight: 700, color, bgcolor: bg }} />;
  };

  const renderRow = (entry, idx) => {
    const id = entry[schema.pk] || entry.id || entry._id;
    if (tableName === 'TAX_DECLARATION') {
      return (
        <TableRow key={id || idx} hover sx={{ '& td': { borderBottom: '1px dashed #E2E8F0' }, '&:last-child td': { borderBottom: 'none' } }}>
          <TableCell sx={{ fontSize: 13, color: '#334155', py: 1 }}>{entry.declaration_id}</TableCell>
          <TableCell sx={{ fontSize: 13, color: '#334155', py: 1 }}>{entry.financial_year}</TableCell>
          <TableCell sx={{ fontSize: 13, color: '#334155', py: 1 }}>{entry.section_code}</TableCell>
          <TableCell align="right" sx={{ fontSize: 13, color: '#334155', py: 1 }}>{entry.declared_amount ? `₹${Number(entry.declared_amount).toLocaleString('en-IN')}` : '—'}</TableCell>
          <TableCell align="right" sx={{ fontSize: 13, color: '#334155', py: 1 }}>{entry.approved_amount ? `₹${Number(entry.approved_amount).toLocaleString('en-IN')}` : '—'}</TableCell>
          <TableCell align="center" sx={{ py: 1 }}>{renderStatus(entry.status)}</TableCell>
          <TableCell align="right" sx={{ py: 1 }}>
            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
              {onView && <Tooltip title="View"><IconButton size="small" onClick={() => onView(entry)} sx={{ color: '#2563EB' }}><VisibilityIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>}
              {onEdit && <Tooltip title="Edit"><IconButton size="small" onClick={() => onEdit(entry)} sx={{ color: '#8b5cf6' }}><EditIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>}
              {onDelete && <Tooltip title="Delete"><IconButton size="small" onClick={() => onDelete(entry)} sx={{ color: '#EF4444' }}><DeleteOutlineIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>}
            </Stack>
          </TableCell>
        </TableRow>
      );
    }
    return (
      <TableRow key={id || idx} hover sx={{ '& td': { borderBottom: '1px dashed #E2E8F0' }, '&:last-child td': { borderBottom: 'none' } }}>
        <TableCell sx={{ fontSize: 13, color: '#334155', py: 1 }}>{entry.config_id}</TableCell>
        <TableCell sx={{ fontSize: 13, color: '#334155', py: 1 }}>{entry.fiscal_year || entry.financial_year}</TableCell>
        <TableCell sx={{ fontSize: 13, color: '#334155', py: 1, fontWeight: 600 }}>{entry.tax_regime || entry.regime_type}</TableCell>
        <TableCell align="right" sx={{ fontSize: 13, color: '#334155', py: 1 }}>{entry.projected_taxable_income ? `₹${Number(entry.projected_taxable_income).toLocaleString('en-IN')}` : '—'}</TableCell>
        <TableCell align="right" sx={{ fontSize: 13, color: '#334155', py: 1 }}>{entry.tds_monthly ? `₹${Number(entry.tds_monthly).toLocaleString('en-IN')}` : '—'}</TableCell>
        <TableCell align="center" sx={{ py: 1 }}>{entry.regime_locked === 'Y' ? <Chip label="LOCKED" size="small" sx={{ height: 20, fontSize: 10, fontWeight: 700, color: '#10B981', bgcolor: '#D1FAE5' }} /> : <Chip label="UNLOCKED" size="small" sx={{ height: 20, fontSize: 10, fontWeight: 700, color: '#64748B', bgcolor: '#F1F5F9' }} />}</TableCell>
        <TableCell align="right" sx={{ py: 1 }}>
          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
            {onView && <Tooltip title="View"><IconButton size="small" onClick={() => onView(entry)} sx={{ color: '#2563EB' }}><VisibilityIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>}
            {onEdit && <Tooltip title="Edit"><IconButton size="small" onClick={() => onEdit(entry)} sx={{ color: '#8b5cf6' }}><EditIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>}
            {onDelete && <Tooltip title="Delete"><IconButton size="small" onClick={() => onDelete(entry)} sx={{ color: '#EF4444' }}><DeleteOutlineIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>}
          </Stack>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, height: '100%' }}>
      {/* ── Pending Alerts ── */}
      {tableName === 'TAX_DECLARATION' && pendingAlerts.filter(a => !hiddenAlerts.includes(a.assignment_id)).map(alert => (
        <Alert
          key={alert.assignment_id}
          severity="warning"
          sx={{ borderRadius: '8px', alignItems: 'center' }}
          action={
            <IconButton size="small" onClick={() => dismissAlert(alert.assignment_id)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        >
          Tax Declaration is pending for Employee: {alert.employee_number} - {alert.employee_name}. Please complete the Tax Declaration.
        </Alert>
      ))}

      {/* ── Toolbar ── */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
        <Box sx={{ flex: 1, maxWidth: { xs: '100%', md: 400 } }}>
          <TextField
            fullWidth size="small" placeholder="Search by employee name or ID..."
            value={search} onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#9CA3AF' }} /></InputAdornment>,
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch('')} sx={{ color: '#9CA3AF' }}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
              sx: { bgcolor: 'white', borderRadius: '10px', fontSize: 13, '& fieldset': { borderColor: '#E2E8F0' } }
            }}
          />
        </Box>
      </Stack>

      {/* ── Person Groups ── */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pb: 10 }}>
        {filteredGroups.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: 'center', borderRadius: '16px', border: '1px dashed #E2E8F0', bgcolor: '#F8FAFC' }} elevation={0}>
            <AccountCircleIcon sx={{ fontSize: 48, color: '#CBD5E1', mb: 2 }} />
            <Typography fontSize={15} fontWeight={600} color="#475569" mb={1}>No Records Found</Typography>
            <Typography fontSize={13} color="#94A3B8">
              No matching records for the current filters.
            </Typography>
          </Paper>
        ) : (
          filteredGroups.map(group => {
            const isExpanded = expandedPersons[group.personKey] || false;
            return (
              <Card key={group.personKey} elevation={0} sx={{ 
                borderRadius: '12px', 
                border: '1px solid',
                borderColor: isExpanded ? '#8b5cf6' : '#E2E8F0',
                transition: 'all 0.2s ease',
                overflow: 'hidden'
              }}>
                <Box
                  onClick={() => togglePerson(group.personKey)}
                  sx={{
                    px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    cursor: 'pointer', bgcolor: isExpanded ? '#f5f3ff' : 'white',
                    '&:hover': { bgcolor: isExpanded ? '#f5f3ff' : '#F8FAFC' }
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{ 
                      width: 36, height: 36, borderRadius: '10px', 
                      bgcolor: isExpanded ? '#8b5cf6' : '#8b5cf6', color: 'white',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 700, fontSize: 13
                    }}>
                      {group.fullName.substring(0, 2).toUpperCase()}
                    </Box>
                    <Box>
                      <Typography fontSize={14} fontWeight={700} color="#111827">
                        {group.fullName}
                        <Typography component="span" fontSize={11} sx={{ ml: 1, px: 1, py: 0.2, bgcolor: '#F1F5F9', color: '#64748B', borderRadius: '4px' }}>
                          {group.assignment_id !== '—' ? group.assignment_id : group.person_id}
                        </Typography>
                      </Typography>
                    </Box>
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={3}>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography fontSize={9.5} fontWeight={700} color="#64748B" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        RECORDS
                      </Typography>
                      <Typography fontSize={14} fontWeight={700} color="#8b5cf6">
                        {group.entries.length}
                      </Typography>
                    </Box>
                    <IconButton size="small" sx={{ color: '#94A3B8' }}>
                      {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                  </Stack>
                </Box>

                <Collapse in={isExpanded}>
                  <Box sx={{ bgcolor: '#F8FAFC', borderTop: '1px solid #E2E8F0' }}>
                    <Table size="small">
                      <TableHead>
                        {renderHeaders()}
                      </TableHead>
                      <TableBody>
                        {group.entries.map((entry, idx) => renderRow(entry, idx))}
                      </TableBody>
                    </Table>
                  </Box>
                </Collapse>
              </Card>
            );
          })
        )}
      </Box>
    </Box>
  );
}
